/**
 * Print-Fixed native exact-total variant resolver.
 *
 * Runtime variant IDs and prices come from Shopify's Liquid product object.
 * The calculator configuration supplies the approved commercial matrix. A
 * selection is purchasable only when the catalog contains one and only one
 * available variant with the expected options and exact cent price.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZugPrintFixedNativeVariants = api;
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  var MATERIAL_OPTION = 'Material';
  var SIDES_OPTION = 'Print Sides';
  var QUANTITY_OPTION = 'Quantity';
  var EXPECTED_VARIANT_COUNT = 32;

  function normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function fail(code, message, details) {
    return {
      ok: false,
      code: code,
      message: message,
      details: details || {}
    };
  }

  function exactCents(value) {
    var number = Number(value);
    return Number.isFinite(number) && number >= 0 && Math.round(number) === number
      ? number
      : null;
  }

  function configuredTotalCents(config, materialKey, sideKey, quantity) {
    var pricing = config && config.pricing;
    var material = config && config.materials && config.materials[materialKey];
    var base = pricing && pricing[String(quantity)];
    if (!material || base === undefined) return null;

    var materialMultiplier = Number(material.multiplier);
    var sideMultiplier = sideKey === 'double'
      ? Number(config.double_sided_multiplier)
      : 1;
    if (!(materialMultiplier > 0) || !(sideMultiplier > 0)) return null;

    return exactCents(Math.round(Number(base) * materialMultiplier * sideMultiplier * 100));
  }

  function expectedMatrix(config) {
    var contract = config && config.native_variant_pricing;
    if (!contract || contract.enabled !== true) return [];

    var quantities = Array.isArray(config.quantities) ? config.quantities : [];
    var materialKeys = Object.keys(config.materials || {});
    var sideKeys = config.supports_double_sided ? ['single', 'double'] : ['single'];
    var sideValues = contract.side_values || {};
    var matrix = [];

    materialKeys.forEach(function (materialKey) {
      sideKeys.forEach(function (sideKey) {
        quantities.forEach(function (quantity) {
          matrix.push({
            materialKey: materialKey,
            materialValue: String(config.materials[materialKey].label || ''),
            sideKey: sideKey,
            sideValue: String(sideValues[sideKey] || ''),
            quantity: Number(quantity),
            quantityValue: String(quantity),
            expectedCents: configuredTotalCents(config, materialKey, sideKey, quantity)
          });
        });
      });
    });

    return matrix;
  }

  function optionNames(catalog) {
    if (!catalog) return [];
    var names = catalog.option_names || catalog.optionNames || catalog.options || [];
    return Array.isArray(names) ? names.map(String) : [];
  }

  function variantOptionValues(variant, count) {
    if (variant && Array.isArray(variant.options)) {
      return variant.options.slice(0, count).map(String);
    }

    var values = [];
    for (var index = 1; index <= count; index += 1) {
      values.push(String(variant && variant['option' + index] != null ? variant['option' + index] : ''));
    }
    return values;
  }

  function optionIndex(names, expectedName) {
    var expected = normalize(expectedName);
    var matches = [];
    names.forEach(function (name, index) {
      if (normalize(name) === expected) matches.push(index);
    });
    return matches.length === 1 ? matches[0] : -1;
  }

  function matrixKey(materialValue, sideValue, quantityValue) {
    return [
      normalize(materialValue),
      normalize(sideValue),
      normalize(quantityValue)
    ].join('|');
  }

  function validateContract(catalog, config) {
    var contract = config && config.native_variant_pricing;
    if (!contract || contract.enabled !== true) {
      return fail('NATIVE_CONTRACT_DISABLED', 'Native exact-total pricing is not enabled for this recipe.');
    }

    var configuredNames = Array.isArray(contract.option_names) ? contract.option_names.map(String) : [];
    var names = optionNames(catalog);
    if (configuredNames.length !== 3 ||
        normalize(configuredNames[0]) !== normalize(MATERIAL_OPTION) ||
        normalize(configuredNames[1]) !== normalize(SIDES_OPTION) ||
        normalize(configuredNames[2]) !== normalize(QUANTITY_OPTION)) {
      return fail('INVALID_CONTRACT_OPTIONS', 'The recipe must declare Material, Print Sides, and Quantity in canonical order.');
    }
    if (names.length !== 3) {
      return fail('INVALID_PRODUCT_OPTION_COUNT', 'The Shopify product must have exactly three native options.', {
        actual: names.length,
        expected: 3
      });
    }

    var indices = {
      material: optionIndex(names, MATERIAL_OPTION),
      sides: optionIndex(names, SIDES_OPTION),
      quantity: optionIndex(names, QUANTITY_OPTION)
    };
    if (indices.material < 0 || indices.sides < 0 || indices.quantity < 0) {
      return fail('PRODUCT_OPTION_MISMATCH', 'Shopify options must be Material, Print Sides, and Quantity exactly once.', {
        optionNames: names
      });
    }

    var matrix = expectedMatrix(config);
    if (matrix.length !== EXPECTED_VARIANT_COUNT || matrix.some(function (entry) {
      return !entry.materialValue || !entry.sideValue || entry.expectedCents === null;
    })) {
      return fail('INVALID_RECIPE_MATRIX', 'The recipe must define exactly 32 complete exact-price combinations.', {
        actual: matrix.length,
        expected: EXPECTED_VARIANT_COUNT
      });
    }

    var variants = catalog && Array.isArray(catalog.variants) ? catalog.variants : [];
    if (variants.length !== EXPECTED_VARIANT_COUNT) {
      return fail('VARIANT_COUNT_MISMATCH', 'Shopify variant count does not match the approved commercial matrix.', {
        actual: variants.length,
        expected: EXPECTED_VARIANT_COUNT
      });
    }

    var ids = {};
    var expectedByKey = {};
    var variantByKey = {};

    for (var matrixIndex = 0; matrixIndex < matrix.length; matrixIndex += 1) {
      var matrixEntry = matrix[matrixIndex];
      var expectedKey = matrixKey(
        matrixEntry.materialValue,
        matrixEntry.sideValue,
        matrixEntry.quantityValue
      );
      if (expectedByKey[expectedKey]) {
        return fail('DUPLICATE_RECIPE_COMBINATION', 'The recipe contains a duplicate commercial combination.', {
          material: matrixEntry.materialValue,
          sides: matrixEntry.sideValue,
          quantity: matrixEntry.quantityValue
        });
      }
      expectedByKey[expectedKey] = matrixEntry;
    }

    for (var variantIndex = 0; variantIndex < variants.length; variantIndex += 1) {
      var variant = variants[variantIndex];
      var variantId = String(variant && variant.id || '');
      if (!variantId || ids[variantId]) {
        return fail('INVALID_VARIANT_IDS', 'Every native variant must have one unique Shopify variant ID.');
      }
      ids[variantId] = true;

      var values = variantOptionValues(variant, names.length);
      var variantKey = matrixKey(
        values[indices.material],
        values[indices.sides],
        values[indices.quantity]
      );
      var expectedEntry = expectedByKey[variantKey];
      if (!expectedEntry) {
        return fail('EXTRA_VARIANT_COMBINATION', 'Shopify contains a native variant outside the approved commercial matrix.', {
          variantId: variantId,
          optionValues: values
        });
      }
      if (variantByKey[variantKey]) {
        return fail('DUPLICATE_VARIANT_COMBINATION', 'An approved commercial combination resolves to more than one Shopify variant.', {
          firstVariantId: String(variantByKey[variantKey].id),
          duplicateVariantId: variantId,
          optionValues: values
        });
      }
      if (variant.available !== true) {
        return fail('VARIANT_UNAVAILABLE', 'Every exact native Shopify variant must be available before activation.', {
          variantId: variantId,
          optionValues: values
        });
      }

      var actualCents = exactCents(variant.price);
      if (actualCents === null || actualCents !== expectedEntry.expectedCents) {
        return fail('VARIANT_PRICE_MISMATCH', 'A native variant price differs from the approved calculator total.', {
          variantId: variantId,
          actualCents: actualCents,
          expectedCents: expectedEntry.expectedCents,
          differenceCents: actualCents === null ? null : actualCents - expectedEntry.expectedCents
        });
      }
      variantByKey[variantKey] = variant;
    }

    var missingKeys = Object.keys(expectedByKey).filter(function (key) {
      return !variantByKey[key];
    });
    if (missingKeys.length) {
      var missing = expectedByKey[missingKeys[0]];
      return fail('MISSING_VARIANT_COMBINATION', 'Shopify is missing an approved native variant combination.', {
        material: missing.materialValue,
        sides: missing.sideValue,
        quantity: missing.quantityValue
      });
    }

    return {
      ok: true,
      indices: indices,
      matrix: matrix,
      variants: variants,
      optionNames: names,
      variantByKey: variantByKey
    };
  }

  function resolve(catalog, config, selection) {
    var validation = validateContract(catalog, config);
    if (!validation.ok) return validation;

    selection = selection || {};
    var materialKey = String(selection.materialKey || '');
    var sideKey = selection.doubleSided === true || selection.sideKey === 'double'
      ? 'double'
      : 'single';
    var quantity = Number(selection.quantity);
    var expected = validation.matrix.filter(function (entry) {
      return entry.materialKey === materialKey &&
        entry.sideKey === sideKey &&
        entry.quantity === quantity;
    });

    if (expected.length !== 1) {
      return fail('UNCONFIGURED_SELECTION', 'The selected material, sides, and quantity are not an approved commercial combination.', {
        materialKey: materialKey,
        sideKey: sideKey,
        quantity: quantity
      });
    }

    var target = expected[0];
    var variant = validation.variantByKey[matrixKey(
      target.materialValue,
      target.sideValue,
      target.quantityValue
    )];

    return {
      ok: true,
      variantId: String(variant.id),
      expectedCents: target.expectedCents,
      cartQuantity: 1,
      displayQuantity: target.quantity,
      materialKey: target.materialKey,
      materialValue: target.materialValue,
      sideKey: target.sideKey,
      sideValue: target.sideValue,
      quantityValue: target.quantityValue
    };
  }

  function auditCatalog(catalog, config) {
    var validation = validateContract(catalog, config);
    if (!validation.ok) {
      return {
        ok: false,
        expectedCount: expectedMatrix(config).length,
        passedCount: 0,
        failures: [validation]
      };
    }

    var failures = [];
    validation.matrix.forEach(function (entry) {
      var result = resolve(catalog, config, {
        materialKey: entry.materialKey,
        sideKey: entry.sideKey,
        quantity: entry.quantity
      });
      if (!result.ok) failures.push(result);
    });

    return {
      ok: failures.length === 0,
      expectedCount: validation.matrix.length,
      passedCount: validation.matrix.length - failures.length,
      failures: failures
    };
  }

  function nativeProperties(options, resolution) {
    var upload = options.upload || {};
    var properties = {
      'Size': options.sizeLabel,
      'Pieces': String(resolution.displayQuantity),
      'Material': resolution.materialValue,
      'Rush': 'Standard',
      '_pricing_engine': 'true',
      '_line_type': 'base',
      '_bundle_quantity_locked': 'true',
      '_bundle_display_quantity': String(resolution.displayQuantity),
      '_is_print_fixed': 'true',
      '_print_fixed_id': options.config.print_id,
      '_print_fixed_native_uid': options.lineUid
    };
    if (resolution.sideKey === 'double') properties._print_fixed_double_sided = 'true';

    properties._artwork_mode = upload.artwork_mode || 'instant_proof';
    if (upload.artwork_mode === 'email_later') {
      properties._artwork_note = upload.note || '';
    } else if (upload.artwork_mode === 'reorder') {
      properties._previous_order_number = upload.previous_order_number || '';
      properties._artwork_note = upload.note || '';
    } else {
      properties._upload_id = upload.upload_id;
      if (upload.original_url) properties._artwork_original_url = upload.original_url;
      properties._dcf_preview_url = upload.preview_url;
      properties._artwork_approved = upload.approved ? 'true' : 'false';
      properties._artwork_note = upload.note || '';
      if (upload.preview_template) properties._preview_template = String(upload.preview_template);
      if (upload.preview_transform) properties._preview_transform = String(upload.preview_transform);
      if (upload._placement_state) properties._placement_state = String(upload._placement_state);
    }

    [
      '_pf_front_upload_id',
      '_pf_front_original_url',
      '_pf_front_preview_url',
      '_pf_back_upload_id',
      '_pf_back_original_url',
      '_pf_back_preview_url'
    ].forEach(function (key) {
      if (upload[key]) properties[key] = String(upload[key]);
    });

    return properties;
  }

  function responseJson(response, fallbackMessage) {
    if (response.ok) return response.json();
    return response.json().then(function (errorJson) {
      throw new Error(errorJson.description || errorJson.message || fallbackMessage || ('HTTP ' + response.status));
    }, function () {
      throw new Error(fallbackMessage || ('HTTP ' + response.status));
    });
  }

  function addExactLine(options) {
    options = options || {};
    var fetchImpl = options.fetch || (root && root.fetch ? root.fetch.bind(root) : null);
    var serializer = options.serializeProperties;
    var totalCents = exactCents(options.calculatorTotalCents);
    var resolution = resolve(options.catalog, options.config, options.selection);

    if (!resolution.ok) return Promise.reject(Object.assign(new Error(resolution.message), resolution));
    if (totalCents === null || resolution.expectedCents !== totalCents) {
      return Promise.reject(Object.assign(new Error('Calculator total and native variant price differ.'), {
        code: 'CALCULATOR_TOTAL_MISMATCH',
        expectedCents: resolution.expectedCents,
        calculatorTotalCents: totalCents
      }));
    }
    if (typeof fetchImpl !== 'function' || typeof serializer !== 'function') {
      return Promise.reject(Object.assign(new Error('Native cart dependencies are unavailable.'), {
        code: 'NATIVE_CART_DEPENDENCY_MISSING'
      }));
    }

    var width = Number(options.width);
    var height = Number(options.height);
    var properties = serializer({
      raw: nativeProperties(options, resolution),
      pricing: {
        version: 'print_fixed_native_v1',
        width_in: width,
        height_in: height,
        area_in2: width * height,
        u_final: totalCents / 100 / resolution.displayQuantity,
        total_desired: totalCents / 100,
        rush_option: 'regular'
      },
      version: 'print_fixed_native_v1',
      quantity: resolution.displayQuantity,
      totalCents: totalCents,
      material: resolution.materialKey,
      rush: 'regular',
      upload: options.upload || {}
    });
    var item = {
      id: resolution.variantId,
      quantity: 1,
      properties: properties
    };
    var addedKey = '';

    function rollback() {
      if (!addedKey) return Promise.resolve();
      var rollbackKey = addedKey;
      addedKey = '';
      return fetchImpl('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: rollbackKey, quantity: 0 }),
        credentials: 'same-origin'
      }).then(function () {}, function () {});
    }

    return fetchImpl('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] }),
      credentials: 'same-origin'
    })
    .then(function (response) {
      return responseJson(response, 'Shopify rejected the native cart line.');
    })
    .then(function (addResponse) {
      var addedLine = addResponse && Array.isArray(addResponse.items) ? addResponse.items[0] : addResponse;
      addedKey = addedLine && addedLine.key ? addedLine.key : '';
      if (!addedKey) throw new Error('Shopify did not return the added native cart line key.');
      return fetchImpl('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
    })
    .then(function (response) {
      return responseJson(response, 'Unable to verify the native cart line.');
    })
    .then(function (cart) {
      var matches = (cart.items || []).filter(function (cartItem) {
        return cartItem.key === addedKey &&
          String(cartItem.variant_id) === String(resolution.variantId);
      });
      var line = matches[0];
      if (matches.length !== 1 || !line || Number(line.quantity) !== 1 ||
          Number(line.final_line_price) !== totalCents) {
        var verificationError = Object.assign(
          new Error('Native cart verification failed; the added line was rolled back.'),
          { code: 'NATIVE_CART_VERIFICATION_FAILED' }
        );
        return rollback().then(function () { throw verificationError; });
      }

      return {
        ok: true,
        line: line,
        cart: cart,
        resolution: resolution,
        calculatorTotalCents: totalCents,
        cartLineTotalCents: Number(line.final_line_price),
        differenceCents: Number(line.final_line_price) - totalCents
      };
    })
    .catch(function (error) {
      if (!addedKey) throw error;
      return rollback().then(function () { throw error; });
    });
  }

  return {
    EXPECTED_VARIANT_COUNT: EXPECTED_VARIANT_COUNT,
    MATERIAL_OPTION: MATERIAL_OPTION,
    SIDES_OPTION: SIDES_OPTION,
    QUANTITY_OPTION: QUANTITY_OPTION,
    auditCatalog: auditCatalog,
    addExactLine: addExactLine,
    configuredTotalCents: configuredTotalCents,
    expectedMatrix: expectedMatrix,
    nativeProperties: nativeProperties,
    resolve: resolve,
    validateContract: validateContract
  };
});
